// ============================================================
// POOL DE MOTS-CLÉS — Génération programmatique
// Combine villes × services × modificateurs × marques
// Cible : 8000+ mots-clés couverts proprement
// Utilisé dans JSON-LD (Schema.org "keywords") + llms.txt
// ============================================================

import { cities, services } from "./local-seo-data";

// Modificateurs commerciaux & temporels (variations naturelles)
const MODIFIERS = [
  "", "urgent", "rapide", "pas cher", "à domicile", "professionnel",
  "24h/24", "7j/7", "jours fériés", "nuit", "weekend",
  "particulier", "entreprise", "PME", "TPE", "indépendant",
  "près de moi", "tarif", "prix", "devis gratuit", "expert", "spécialiste",
];

// Marques PC / portables couvertes
const PC_BRANDS = [
  "HP", "Dell", "Asus", "Lenovo", "Acer", "MSI", "Apple MacBook",
  "Samsung", "Microsoft Surface", "Razer", "Gigabyte", "Toshiba",
  "Packard Bell", "Medion", "Fujitsu", "LDLC", "Materiel.net",
];

// Composants & marques hardware
const HARDWARE_BRANDS = [
  "NVIDIA RTX", "AMD Radeon", "Intel Core i9", "Intel Core i7", "AMD Ryzen 9",
  "AMD Ryzen 7", "Samsung SSD", "Western Digital", "Seagate", "Crucial",
  "Corsair", "G.Skill", "Kingston", "be quiet!", "Noctua",
];

// Logiciels / OS / problèmes courants
const PROBLEMS = [
  "écran bleu", "écran noir", "ne s'allume plus", "lent", "qui rame",
  "surchauffe", "ventilateur bruyant", "batterie HS", "clavier cassé",
  "écran cassé", "wifi ne marche pas", "imprimante", "scanner",
  "mot de passe Windows oublié", "récupération photos", "virus",
  "ransomware", "phishing", "mise à jour Windows", "boot impossible",
  "BSOD", "Windows 11 installation", "double boot Linux",
];

// Verbes/actions web
const WEB_ACTIONS = [
  "création site internet", "refonte site web", "site vitrine", "site e-commerce",
  "boutique en ligne", "agence web", "développeur web", "WordPress",
  "Shopify", "PrestaShop", "référencement Google", "SEO local",
  "optimisation site web", "hébergement web", "nom de domaine",
  "maintenance site web", "site responsive mobile", "PWA application web",
];

// Génère toutes les combinaisons service × ville × modificateur
function generateServiceCityKeywords(): string[] {
  const out = new Set<string>();
  for (const s of services) {
    const baseName = s.name.toLowerCase();
    const shortName = s.shortName.toLowerCase();
    for (const c of cities) {
      const city = c.name;
      for (const mod of MODIFIERS) {
        if (mod) {
          out.add(`${baseName} ${mod} ${city}`);
          out.add(`${shortName} ${mod} ${city}`);
        } else {
          out.add(`${baseName} ${city}`);
          out.add(`${shortName} ${city}`);
          out.add(`${baseName} ${city} ${c.postalCode}`);
        }
      }
    }
  }
  return [...out];
}

// Génère "réparation MARQUE à VILLE"
function generateBrandCityKeywords(): string[] {
  const out = new Set<string>();
  const actions = ["réparation", "dépannage", "diagnostic", "écran", "clavier", "batterie", "installation"];
  for (const brand of PC_BRANDS) {
    for (const c of cities.slice(0, 35)) {
      for (const act of actions) {
        out.add(`${act} ${brand} ${c.name}`);
      }
    }
  }
  return [...out];
}

// Génère problèmes courants × ville
function generateProblemKeywords(): string[] {
  const out = new Set<string>();
  for (const p of PROBLEMS) {
    for (const c of cities) {
      out.add(`${p} ${c.name}`);
      out.add(`mon PC ${p} ${c.name}`);
    }
  }
  return [...out];
}

// Génère actions web × ville
function generateWebKeywords(): string[] {
  const out = new Set<string>();
  for (const a of WEB_ACTIONS) {
    for (const c of cities) {
      out.add(`${a} ${c.name}`);
      out.add(`${a} ${c.postalCode}`);
    }
  }
  return [...out];
}

// Génère "composant marque" + "PC gaming/bureautique/pro"
function generateHardwareKeywords(): string[] {
  const out = new Set<string>();
  const configs = ["PC gaming", "PC bureautique", "PC création", "PC streaming", "PC pro", "station de travail", "PC silencieux", "mini PC"];
  for (const conf of configs) {
    for (const c of cities) {
      out.add(`${conf} sur mesure ${c.name}`);
      out.add(`montage ${conf} ${c.name}`);
    }
    for (const hw of HARDWARE_BRANDS) {
      out.add(`${conf} ${hw}`);
    }
  }
  return [...out];
}

// Mots-clés génériques métier (sans ville)
const GENERIC_KEYWORDS = [
  "dépanneur informatique Hérault", "informaticien à domicile", "SOS informatique",
  "réparateur ordinateur portable", "réparateur PC fixe", "expert informatique Occitanie",
  "agence web Hérault", "agence web Occitanie", "création site internet Hérault",
  "monteur PC gaming France", "assembleur PC sur mesure", "configurateur PC en ligne",
  "vidéosurveillance professionnelle", "installation caméra IP", "réseau informatique entreprise",
  "infogérance TPE PME", "maintenance informatique entreprise", "sauvegarde cloud entreprise",
  "récupération données disque dur", "récupération SSD NVMe", "désinfection ransomware",
  "formation informatique senior", "cours Excel à domicile", "cours Word particulier",
  "domotique Hérault", "maison connectée Béziers", "Philips Hue installateur",
  "audit Wifi gratuit", "Wifi mesh installation", "câblage Ethernet CAT6 pro",
  "PWA développeur freelance", "application web sur mesure", "site responsive mobile first",
  "SEO local Béziers", "référencement Google maps", "fiche Google Business",
  "consultant Lovable", "développeur React TypeScript", "freelance Supabase",
  // ===== Requêtes réelles GSC (impressions confirmées) =====
  "informatique beziers", "magasin informatique beziers", "informatique béziers",
  "boutique informatique beziers", "magasin pc beziers", "pc beziers",
  "informatique beziers 34500", "magasin informatique béziers",
  "dépannage informatique beziers", "dépannage informatique béziers",
  "depannage informatique beziers", "depannage informatique montady 34",
  "réparation informatique beziers", "réparation informatique béziers",
  "reparateur pc beziers", "réparateur d ordinateur beziers",
  "réparation ordinateur beziers", "réparation ordinateur autour de moi",
  "informaticien beziers", "informatique autour de moi",
  "entreprise informatique beziers", "societe informatique beziers", "service informatique béziers",
  "magasin spécialisé en informatique", "magasin informatique thezan les beziers",
  "informatique lignan sur orb", "informatique maraussan", "informatique servian",
  "dépannage informatique sauvian", "dépannage informatique à maraussan",
  "bezier réparation informatique domicile", "dépannage informatique autour de moi",
  "maintenance informatique beziers les mieux notés",
  "upgrade pc beziers", "booster pc lent beziers",
  "remplacement ssd beziers", "reparation disque dure",
  "computereinkel puisserguier", "réparation pc portable",
  "ordinateur portable pas cher", "ordi portable", "imprimante portable",
  "electronique beziers", "développeur informatique", "tech informatique", "it consultant",
  "sudweb-tech dépannage informatique aux alentours de béziers avis",
  "sudweb-tech dépannage informatique et créations de site aux alentours de béziers avis",
];

// ===== Mots-clés bonus : déclinaisons spécifiques aux nouveaux services =====
const NEW_SERVICE_KEYWORDS = [
  // Upgrade & optimisation PC
  "upgrade SSD NVMe Béziers", "remplacement disque dur par SSD Béziers", "ajout RAM PC Béziers",
  "PC lent que faire", "accélérer PC lent Béziers", "optimisation Windows Béziers",
  "réinstallation Windows Béziers", "PC qui rame solution", "upgrade carte graphique Béziers",
  "nettoyage PC surchauffe Béziers", "changement pâte thermique PC Béziers",
  // Imprimante
  "réparation imprimante HP Béziers", "imprimante Epson tête bouchée",
  "imprimante Canon bourrage papier", "installation imprimante Wifi",
  "imprimante Brother ne s'allume plus", "imprimante laser HS",
  "configuration imprimante réseau", "pilote imprimante Mac",
  // Vente / magasin
  "magasin informatique Béziers ouvert", "boutique informatique Béziers centre",
  "PC reconditionné Béziers garanti", "Dell Latitude reconditionné Béziers",
  "HP EliteBook reconditionné Béziers", "Lenovo ThinkPad reconditionné",
  "MacBook reconditionné Béziers", "écran PC pas cher Béziers",
  "achat PC bureautique Béziers", "achat PC gaming Béziers",
  "livraison PC à domicile Hérault", "installation PC neuf à domicile",
];


let _cachedPool: string[] | null = null;

export function getKeywordsPool(): string[] {
  if (_cachedPool) return _cachedPool;
  const all = new Set<string>([
    ...generateServiceCityKeywords(),
    ...generateBrandCityKeywords(),
    ...generateProblemKeywords(),
    ...generateWebKeywords(),
    ...generateHardwareKeywords(),
    ...GENERIC_KEYWORDS,
    ...NEW_SERVICE_KEYWORDS,
  ]);
  _cachedPool = [...all];
  return _cachedPool;
}

export function getKeywordsCount(): number {
  return getKeywordsPool().length;
}

// Pour la balise <meta name="keywords"> : top 50 les plus stratégiques
export function getTopKeywordsForMeta(): string {
  const top = [
    // ⭐ Top GSC réels (impressions confirmées Search Console)
    "informatique Béziers", "magasin informatique Béziers", "magasin PC Béziers",
    "boutique informatique Béziers", "informaticien Béziers",
    "dépannage informatique Béziers", "réparation informatique Béziers",
    "réparation ordinateur Béziers", "réparateur PC Béziers",
    "service informatique Béziers", "entreprise informatique Béziers",
    "upgrade SSD Béziers", "PC lent Béziers",
    "réparation imprimante Béziers", "PC reconditionné Béziers",
    // Disponibilité
    "dépannage informatique 24h/24", "dépannage informatique 7j/7",
    "dépannage informatique jours fériés", "dépannage informatique à domicile Béziers",
    // Services cœur
    "PC sur mesure Béziers", "PC gaming sur mesure Béziers",
    "création site web Béziers", "agence web Béziers", "SEO local Béziers",
    "récupération données Béziers", "installation caméra Béziers", "infogérance TPE Béziers",
    // Villes voisines
    "dépannage informatique Narbonne", "dépannage informatique Agde",
    "dépannage informatique Sète", "dépannage informatique Montpellier",
    "dépannage informatique Pézenas", "dépannage informatique Valras-Plage",
    "informatique Maraussan", "informatique Lignan-sur-Orb", "informatique Servian",
    "informatique Montady", "informatique Sauvian", "informatique Thézan-lès-Béziers",
    // Marques
    "réparation MacBook Béziers", "réparation HP Béziers", "réparation Dell Béziers",
    "réparation Lenovo Béziers", "réparation Asus Béziers",
    "SUDWEB-TECH",
  ];
  return top.join(", ");
}
